import './globals.css'
import './i18n'

import { ClassifierProvider } from '@jobilla/classifier'
import { logger } from '@jobilla/instrumentation'
import { axiosInstance } from '@jobilla/nexus-sdk/lib/client'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRouter, ErrorComponentProps, RouterProvider } from '@tanstack/react-router'
import { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorState } from './components/ErrorState'
import { PostHogProviderWrapper } from './PostHogProviderWrapper'
import { routeTree } from './routeTree.gen'

axiosInstance.defaults.baseURL = import.meta.env.VITE_NEXUS_API_URL
axiosInstance.defaults.withCredentials = true

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

if (SENTRY_DSN) {
	Sentry.init({
		dsn: SENTRY_DSN,
		environment: import.meta.env.VITE_APP_ENV,
		debug: import.meta.env.DEV,
		enabled: import.meta.env.PROD,
	})
}

const queryClient = new QueryClient()

const router = createRouter({
	routeTree,
	context: { queryClient },
	defaultPreload: 'intent',
	defaultPreloadStaleTime: 0,
	scrollRestoration: true,
	defaultErrorComponent: RouteError,
})

declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}

function RouteError({ error }: ErrorComponentProps) {
	useEffect(() => {
		logger.error(error, { tags: { error_boundary: 'route' } })
	}, [error])

	return <ErrorState />
}

createRoot(document.getElementById('root')!).render(
	<ErrorBoundary
		fallback={<ErrorState />}
		onError={(error, info) => {
			logger.error(error, {
				tags: { error_boundary: 'app' },
				data: { componentStack: info.componentStack },
			})
		}}
	>
		<QueryClientProvider client={queryClient}>
			<ClassifierProvider url={import.meta.env.VITE_CLASSIFIER_URL}>
				<PostHogProviderWrapper>
					<RouterProvider router={router} />
				</PostHogProviderWrapper>
			</ClassifierProvider>
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>
	</ErrorBoundary>,
)
